import { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import {
  Building, User, Mail, Phone, Calendar, CheckCircle, X, AlertTriangle,
  MapPin, Search, Clipboard, Check, Send, Target, Map as MapIcon, Loader
} from 'lucide-react';
import { useBusinessRegistration } from '../../authentication_components/businessRegistrationContext';
import { useEmailVerification } from '../../authentication_components/emailverificationCodeContext';
// 1. IMPORT TRACKER CONTEXTS
import { useAdminTracker } from '../../authentication_components/adminTrackerContext';
import { useAuth } from '../../authentication_components/authContext';
import businessCategories from './utils/business_categories';
import puertoRicoCities from './business_registrations_subcomponents/pr_cities';

// --- Google Maps Configuration ---
const GOOGLE_MAPS_API_KEY = "AIzaSyBG8nQzX8m4ZSw9wYepF2idmupWAzVHbBM";

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '0.5rem'
};

const defaultCenter = {
  lat: 18.2208,
  lng: -66.5901
};

export default function RegisterBusiness() {
  const {
    registerBusiness,
    addBusinessLocation,
    validateRegistrationData,
    checkEmailExists,
    checkPhoneExists,
    loading,
    error,
    clearError,
    isAdminUser,
    isMasterUser,
    hasManagementAccess
  } = useBusinessRegistration();

  const {
    sendAndGetVerificationCode,
    loading: verificationLoading,
    error: verificationError,
    success: verificationSuccess,
    clearMessages: clearVerificationMessages,
  } = useEmailVerification();

  // 2. INITIALIZE TRACKER HOOKS
  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();

  // Load Google Maps Script
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  });

  const [businessData, setBusinessData] = useState({
    businessName: '',
    businessYear: '',
    businessCategory: '',
    businessOwnerFirstName: '',
    businessOwnerLastName: '',
    businessEmail: '',
    businessPhone: '',
    businessAddress: '',
    businessAddressSecondary: '',
    businessCity: '',
    businessZip: '',
    businessScenery: '',
    businessDescription: '',
    deviceType: 'web',
    referralSource: 'admin_registration'
  });

  // Location Logic Updates
  const [coordinates, setCoordinates] = useState(null);
  const [showMapDialog, setShowMapDialog] = useState(false);

  // Temporary state for the Map Modal
  const [tempCoordinates, setTempCoordinates] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);

  const [errors, setErrors] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copied, setCopied] = useState(false);

  // Category search functionality
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // City search functionality
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  // Real-time validation states
  const [emailChecking, setEmailChecking] = useState(false);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState(null);

  // State for the send code timer
  const [countdown, setCountdown] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1899 }, (_, i) => currentYear - i);

  const filteredCategories = businessCategories.filter(category =>
    category.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const filteredCities = puertoRicoCities.filter(city =>
    city.toLowerCase().includes(citySearch.toLowerCase())
  );

  const establishmentTypes = [
    'Local Comercial', 'Plaza Comercial', 'Centro Comercial', 'Kiosco',
    'Food Truck', 'Casa/Residencia', 'Oficina', 'Almacén', 'Otro'
  ];

  const formatPhoneNumber = (phoneNumberString) => {
    let cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    if (cleaned.length > 10) cleaned = cleaned.substring(0, 10);
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phoneNumberString;
  };

  const validateEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(String(email).toLowerCase());

  const checkEmailExistence = async (email) => {
    if (!email || !validateEmail(email)) return;
    setEmailChecking(true);
    try {
      setEmailExists(await checkEmailExists(email));
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailExists(false);
    } finally {
      setEmailChecking(false);
    }
  };

  const checkPhoneExistence = async (phone) => {
    const cleanedPhone = phone.replace(/\D/g, '');
    if (!cleanedPhone || cleanedPhone.length !== 10) return;
    setPhoneChecking(true);
    try {
      setPhoneExists(await checkPhoneExists(cleanedPhone));
    } catch (error) {
      console.error('Error checking phone:', error);
      setPhoneExists(false);
    } finally {
      setPhoneChecking(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => businessData.businessEmail ? checkEmailExistence(businessData.businessEmail) : setEmailExists(false), 500);
    return () => clearTimeout(timeoutId);
  }, [businessData.businessEmail]);

  useEffect(() => {
    const timeoutId = setTimeout(() => businessData.businessPhone ? checkPhoneExistence(businessData.businessPhone) : setPhoneExists(false), 500);
    return () => clearTimeout(timeoutId);
  }, [businessData.businessPhone]);

  useEffect(() => {
    let timer;
    if (isTimerActive && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prevCountdown => prevCountdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setIsTimerActive(false);
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [isTimerActive, countdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'businessEmail') {
      setIsTimerActive(false);
      setCountdown(0);
      clearVerificationMessages();
      setVerificationCode(null);
    }

    if (name === 'businessPhone') newValue = formatPhoneNumber(value);
    else if (['businessOwnerFirstName', 'businessOwnerLastName'].includes(name)) newValue = value.replace(/[^a-zA-ZüéáíóúñÑÜÁÉÍÚÓ\s]/g, '');
    else if (name === 'businessZip') newValue = value.replace(/[^0-9-]/g, '');

    setBusinessData(prev => ({ ...prev, [name]: newValue }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (error) clearError();
  };

  const handleSendCode = async () => {
    if (!validateEmail(businessData.businessEmail) || emailExists || isTimerActive) {
      return;
    }
    try {
      const result = await sendAndGetVerificationCode(businessData.businessEmail);
      if (result.code) {
        setVerificationCode(result.code);
      }
      setIsTimerActive(true);
      setCountdown(120);
    } catch (err) {
      console.error("Failed to send verification code:", err);
      setVerificationCode(null);
    }
  };

  // --- Map Logic Functions ---

  const openMapDialog = () => {
    setShowMapDialog(true);
    // If we already have coordinates, center the map there, otherwise default
    if (coordinates) {
      setTempCoordinates(coordinates);
      setMapCenter({ lat: coordinates.latitude, lng: coordinates.longitude });
    } else {
      setTempCoordinates(null);
      setMapCenter(defaultCenter);
    }
    setMapError(null);
  };

  const closeMapDialog = () => {
    setShowMapDialog(false);
    setMapError(null);
  };

  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setTempCoordinates({ latitude: lat, longitude: lng });
  }, []);

  const confirmMapSelection = () => {
    setCoordinates(tempCoordinates);
    closeMapDialog();
  };

  const fetchCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMapError('La geolocalización no es soportada por este navegador.');
      return;
    }

    setMapLoading(true);
    setMapError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newCoords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setTempCoordinates(newCoords);
        setMapCenter({ lat: newCoords.latitude, lng: newCoords.longitude });
        setMapLoading(false);
      },
      (error) => {
        let errorMessage = 'Ocurrió un error al obtener la ubicación.';
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage = 'Permiso de ubicación denegado. Revise la configuración de su navegador.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = 'La información de la ubicación no está disponible.';
        } else if (error.code === error.TIMEOUT) {
          errorMessage = 'La solicitud de ubicación expiró.';
        }
        setMapError(errorMessage);
        setMapLoading(false);
      }
    );
  };

  const handleCategorySearch = (e) => {
    const value = e.target.value;
    setCategorySearch(value);
    setShowCategoryDropdown(true);
    if (businessData.businessCategory && !value) setBusinessData(prev => ({ ...prev, businessCategory: '' }));
  };

  const selectCategory = (category) => {
    setBusinessData(prev => ({ ...prev, businessCategory: category }));
    setCategorySearch(category);
    setShowCategoryDropdown(false);
    if (errors.businessCategory) setErrors(prev => ({ ...prev, businessCategory: '' }));
  };

  const handleCitySearch = (e) => {
    const value = e.target.value;
    setCitySearch(value);
    setShowCityDropdown(true);
    if (businessData.businessCity && !value) setBusinessData(prev => ({ ...prev, businessCity: '' }));
  };

  const selectCity = (city) => {
    setBusinessData(prev => ({ ...prev, businessCity: city }));
    setCitySearch(city);
    setShowCityDropdown(false);
    if (errors.businessCity) setErrors(prev => ({ ...prev, businessCity: '' }));
  };

  const generateTemporaryPassword = () => {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+~`|}{[]:;?><,./-=';
    const allChars = upper + lower + numbers + symbols;
    let password = '';
    password += upper[Math.floor(Math.random() * upper.length)];
    password += lower[Math.floor(Math.random() * lower.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    for (let i = password.length; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    return password.split('').sort(() => 0.5 - Math.random()).join('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!businessData.businessName || businessData.businessName.trim().length === 0) newErrors.businessName = 'El nombre del negocio es requerido';
    const year = parseInt(businessData.businessYear, 10);
    if (isNaN(year) || year < 1900 || year > currentYear) newErrors.businessYear = `El año debe estar entre 1900 y ${currentYear}`;
    if (!businessData.businessOwnerFirstName || businessData.businessOwnerFirstName.trim().length === 0) newErrors.businessOwnerFirstName = 'El nombre es requerido';
    if (!businessData.businessOwnerLastName || businessData.businessOwnerLastName.trim().length === 0) newErrors.businessOwnerLastName = 'El apellido es requerido';
    if (!businessData.businessCategory) newErrors.businessCategory = 'Debe seleccionar una categoría';
    if (!businessData.businessEmail) newErrors.businessEmail = 'El correo electrónico es requerido';
    else if (!validateEmail(businessData.businessEmail)) newErrors.businessEmail = 'Formato de correo electrónico inválido';
    else if (emailExists) newErrors.businessEmail = 'Este correo electrónico ya está registrado';
    const cleanedPhone = businessData.businessPhone.replace(/\D/g, '');
    if (!businessData.businessPhone) newErrors.businessPhone = 'El número de teléfono es requerido';
    else if (cleanedPhone.length !== 10) newErrors.businessPhone = 'El número de teléfono debe tener 10 dígitos';
    else if (phoneExists) newErrors.businessPhone = 'Este número de teléfono ya está registrado';
    if (!businessData.businessAddress || businessData.businessAddress.trim().length === 0) newErrors.businessAddress = 'La dirección es requerida';
    if (!businessData.businessCity || businessData.businessCity.trim().length === 0) newErrors.businessCity = 'La ciudad es requerida';
    if (!businessData.businessZip || businessData.businessZip.trim().length === 0) newErrors.businessZip = 'El código postal es requerido';
    if (!businessData.businessScenery) newErrors.businessScenery = 'Debe seleccionar el tipo de establecimiento';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const tempPassword = generateTemporaryPassword();
      setGeneratedPassword(tempPassword);
      setShowConfirmDialog(true);
    }
  };

  const confirmRegistration = async () => {
    try {
      const registrationPayload = {
        business_name: businessData.businessName,
        business_year: parseInt(businessData.businessYear),
        business_category: businessData.businessCategory,
        business_owner: `${businessData.businessOwnerFirstName} ${businessData.businessOwnerLastName}`,
        business_email: businessData.businessEmail,
        business_phone: businessData.businessPhone.replace(/\D/g, ''),
        password: generatedPassword,
        business_terms: true,
        business_description: businessData.businessDescription || `Negocio registrado por administrador. Categoría: ${businessData.businessCategory}. Tipo: ${businessData.businessScenery}.`,
        device_type: businessData.deviceType,
        referral_source: businessData.referralSource,
        ...(hasManagementAccess && {
          phone_verified: phoneVerified,
          email_verified: emailVerified
        })
      };

      const validationErrors = validateRegistrationData(registrationPayload);
      if (validationErrors.length > 0) {
        setErrors(validationErrors.reduce((acc, err) => ({ ...acc, [err.field]: err.message }), {}));
        setShowConfirmDialog(false);
        return;
      }

      // 1. Perform Registration
      const result = await registerBusiness(registrationPayload);

      if (result && result.business_registry_number) {
        try {
          // 2. Add Location (if applicable)
          const lat = coordinates ? coordinates.latitude : 18.2208;
          const lon = coordinates ? coordinates.longitude : -66.5901;

          const locationPayload = {
            location_latitude: lat,
            location_longitude: lon,
            location_address_1: businessData.businessAddress,
            location_address_2: businessData.businessAddressSecondary || null,
            location_city: businessData.businessCity,
            location_country: 'Puerto Rico',
            location_zip: businessData.businessZip,
            location_scenery_type: businessData.businessScenery,
            location_count: 1,
            location_main: true,
            location_visibility: true
          };
          await addBusinessLocation(result.business_registry_number, locationPayload);
        } catch (locationError) {
          console.warn('Failed to add location (business still registered):', locationError);
        }

        // 3. TRACK ACTION (Safe-Fail Pattern)
        try {
          await createTracker({
            admin_id: adminId || 'unknown',
            change_type: 'business_registration',
            change_reason: `New Business Registered: ${result.business_name} (${result.business_registry_number}) - Owner: ${registrationPayload.business_owner} - Category: ${registrationPayload.business_category} - Created by Admin: ${adminId}`
          });
          console.log('[AdminTracker] Business registration tracked successfully');
        } catch (trackerErr) {
          console.error('[AdminTracker] Failed to track registration:', trackerErr);
        }
      }

      setRegistrationResult(result);
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
      resetForm();

    } catch (regError) {
      setShowConfirmDialog(false);
      const errorMessage = regError.message || 'Error al registrar el negocio. Por favor intente nuevamente.';
      if (errorMessage.toLowerCase().includes('email')) setErrors(prev => ({ ...prev, businessEmail: 'Este correo electrónico ya está registrado' }));
      else if (errorMessage.toLowerCase().includes('phone')) setErrors(prev => ({ ...prev, businessPhone: 'Este número de teléfono ya está registrado' }));
      else alert(errorMessage);
    }
  };

  const resetForm = () => {
    setBusinessData({
      businessName: '', businessYear: '', businessCategory: '', businessOwnerFirstName: '',
      businessOwnerLastName: '', businessEmail: '', businessPhone: '', businessAddress: '',
      businessAddressSecondary: '', businessCity: '', businessZip: '', businessScenery: '',
      businessDescription: '', deviceType: 'web', referralSource: 'admin_registration'
    });
    setCategorySearch('');
    setCitySearch('');
    setErrors({});
    setEmailExists(false);
    setPhoneExists(false);
    setEmailVerified(false);
    setPhoneVerified(false);
    setGeneratedPassword('');
    setCopied(false);
    clearError();
    clearVerificationMessages();
    setVerificationCode(null);
    setCountdown(0);
    setIsTimerActive(false);
    // Reset location state
    setCoordinates(null);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cancelRegistration = () => setShowConfirmDialog(false);
  const closeSuccessDialog = () => {
    setShowSuccessDialog(false);
    setRegistrationResult(null);
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-orange-100 min-h-screen">
      <div className="bg-white border-b border-orange-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <Building className="h-6 w-6 text-orange-500 mr-3" />
            Registrar Nuevo Negocio
          </h2>
          <p className="text-gray-600 mt-2">
            Complete todos los campos para registrar un nuevo negocio en la plataforma
          </p>
        </div>
      </div>

      {error && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
              <div>
                <p className="text-red-800 font-medium">Error en el registro</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
              <button onClick={clearError} className="ml-auto text-red-400 hover:text-red-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-xl border border-orange-200 p-8">
          <div className="space-y-6">

            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Información del Negocio
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Negocio *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type="text"
                      name="businessName"
                      value={businessData.businessName}
                      onChange={handleChange}
                      maxLength="255"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 ${errors.businessName ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="Ingrese el nombre del negocio"
                    />
                  </div>
                  {errors.businessName && <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Año de Establecimiento *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-orange-400" />
                    </div>
                    <select
                      name="businessYear"
                      value={businessData.businessYear}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 ${errors.businessYear ? 'border-red-300' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Seleccione un año</option>
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  {errors.businessYear && <p className="mt-1 text-sm text-red-600">{errors.businessYear}</p>}
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categoría del Negocio *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <Search className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type="text"
                      value={categorySearch}
                      onChange={handleCategorySearch}
                      onFocus={() => setShowCategoryDropdown(true)}
                      maxLength="255"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 ${errors.businessCategory ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="Buscar categoría..."
                    />
                  </div>

                  {showCategoryDropdown && (
                    <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCategories.length > 0 ? (
                        filteredCategories.map((category, index) => (
                          <div
                            key={index}
                            onClick={() => selectCategory(category)}
                            className="px-4 py-2 hover:bg-orange-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                          >
                            {category}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          No se encontraron categorías
                        </div>
                      )}
                    </div>
                  )}
                  {errors.businessCategory && <p className="mt-1 text-sm text-red-600">{errors.businessCategory}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción del Negocio (Opcional)
                  </label>
                  <textarea
                    name="businessDescription"
                    value={businessData.businessDescription}
                    onChange={handleChange}
                    maxLength="2000"
                    rows="3"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none ${errors.businessDescription ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Descripción breve del negocio..."
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Información del Propietario
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type="text"
                      name="businessOwnerFirstName"
                      value={businessData.businessOwnerFirstName}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 border-gray-300"
                      placeholder="Nombre del propietario"
                    />
                  </div>
                  {errors.businessOwnerFirstName && <p className="mt-1 text-sm text-red-600">{errors.businessOwnerFirstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Apellido *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type="text"
                      name="businessOwnerLastName"
                      value={businessData.businessOwnerLastName}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 border-gray-300"
                      placeholder="Apellido del propietario"
                    />
                  </div>
                  {errors.businessOwnerLastName && <p className="mt-1 text-sm text-red-600">{errors.businessOwnerLastName}</p>}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Información de Contacto
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Correo Electrónico *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-orange-400" />
                    </div>
                    <input type="email" name="businessEmail" value={businessData.businessEmail} onChange={handleChange} className="w-full pl-10 pr-4 py-3 border rounded-lg border-gray-300" placeholder="correo@negocio.com" />
                  </div>
                  <div className="mt-2 space-y-2">
                    <button type="button" onClick={handleSendCode} disabled={isTimerActive || verificationLoading} className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-800 hover:bg-black focus:outline-none disabled:bg-gray-400">
                    </button>
                    {verificationCode && <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg"><p className="text-lg font-mono font-bold text-green-900">{verificationCode}</p></div>}
                  </div>
                  {errors.businessEmail && <p className="mt-1 text-sm text-red-600">{errors.businessEmail}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Número de Teléfono *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-orange-400" />
                    </div>
                    <input type="tel" name="businessPhone" value={businessData.businessPhone} onChange={handleChange} className="w-full pl-10 pr-4 py-3 border rounded-lg border-gray-300" placeholder="(787) 555-0123" />
                  </div>
                  {errors.businessPhone && <p className="mt-1 text-sm text-red-600">{errors.businessPhone}</p>}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Información de Ubicación
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dirección Principal *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type="text"
                      name="businessAddress"
                      value={businessData.businessAddress}
                      onChange={handleChange}
                      maxLength="255"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 ${errors.businessAddress ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="Calle, número, urbanización, etc."
                    />
                  </div>
                  {errors.businessAddress && <p className="mt-1 text-sm text-red-600">{errors.businessAddress}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dirección Secundaria (Opcional)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin className="h-5 w-5 text-orange-400" />
                    </div>
                    <input
                      type="text"
                      name="businessAddressSecondary"
                      value={businessData.businessAddressSecondary}
                      onChange={handleChange}
                      maxLength="255"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 ${errors.businessAddressSecondary ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="Apartamento, suite, local, etc."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ciudad *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                        <MapPin className="h-5 w-5 text-orange-400" />
                      </div>
                      <input
                        type="text"
                        value={citySearch}
                        onChange={handleCitySearch}
                        onFocus={() => setShowCityDropdown(true)}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 ${errors.businessCity ? 'border-red-300' : 'border-gray-300'
                          }`}
                        placeholder="Buscar ciudad..."
                      />
                    </div>

                    {showCityDropdown && (
                      <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredCities.length > 0 ? (
                          filteredCities.map((city, index) => (
                            <div
                              key={index}
                              onClick={() => selectCity(city)}
                              className="px-4 py-2 hover:bg-orange-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                            >
                              {city}
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-sm text-gray-500">
                            No se encontraron ciudades
                          </div>
                        )}
                      </div>
                    )}
                    {errors.businessCity && <p className="mt-1 text-sm text-red-600">{errors.businessCity}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Código Postal *
                    </label>
                    <input
                      type="text"
                      name="businessZip"
                      value={businessData.businessZip}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${errors.businessZip ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="00901"
                    />
                    {errors.businessZip && <p className="mt-1 text-sm text-red-600">{errors.businessZip}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Establecimiento *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-orange-400" />
                    </div>
                    <select
                      name="businessScenery"
                      value={businessData.businessScenery}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 ${errors.businessScenery ? 'border-red-300' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Seleccione el tipo</option>
                      {establishmentTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  {errors.businessScenery && <p className="mt-1 text-sm text-red-600">{errors.businessScenery}</p>}
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Localización Exacta (Coordenadas)
                  </label>

                  <button
                    type="button"
                    onClick={openMapDialog}
                    className="w-full md:w-auto flex items-center justify-center px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                  >
                    <MapIcon className="h-5 w-5 mr-2" />
                    {coordinates ? 'Actualizar Localización Exacta' : 'Seleccionar en Mapa'}
                  </button>

                  {coordinates && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm flex justify-between items-center animate-fadeIn">
                      <div>
                        <p className="font-medium text-green-800 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Ubicación seleccionada
                        </p>
                        <p className="text-green-700 font-mono text-xs mt-1 ml-6">
                          {coordinates.latitude.toFixed(6)}, {coordinates.longitude.toFixed(6)}
                        </p>
                      </div>
                      <button
                        onClick={() => setCoordinates(null)}
                        className="text-gray-400 hover:text-red-500 p-1"
                        title="Eliminar ubicación"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-black text-white py-3 px-6 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    Registrando...
                  </div>
                ) : 'Registrar Negocio'}
              </button>
            </div>
          </div>
        </div>
      </main>

      {showMapDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <MapIcon className="h-5 w-5 text-orange-500 mr-2" />
                Seleccionar Localización Exacta
              </h3>
              <button onClick={closeMapDialog} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4 relative flex-grow overflow-y-auto">
              <p className="text-sm text-gray-600 mb-4">
                Use el botón para obtener su ubicación actual o haga clic en el mapa para colocar el marcador manualmente.
              </p>

              <div className="rounded-lg overflow-hidden border border-gray-300 relative">
                {isMapLoaded ? (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={12}
                    onClick={handleMapClick}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: false,
                    }}
                  >
                    {tempCoordinates && (
                      <Marker position={{ lat: tempCoordinates.latitude, lng: tempCoordinates.longitude }} />
                    )}
                  </GoogleMap>
                ) : (
                  <div className="h-[400px] flex items-center justify-center bg-gray-100 text-gray-500">
                    <Loader className="h-8 w-8 animate-spin mr-2" />
                    Cargando Mapa...
                  </div>
                )}

                <button
                  onClick={fetchCurrentLocation}
                  disabled={mapLoading}
                  className="absolute top-4 right-4 bg-white text-gray-700 px-4 py-2 rounded-lg shadow-lg text-sm font-medium border border-gray-200 flex items-center hover:bg-gray-50 transition-colors disabled:opacity-75"
                >
                  {mapLoading ? (
                    <Loader className="h-4 w-4 animate-spin mr-2 text-orange-500" />
                  ) : (
                    <Target className="h-4 w-4 mr-2 text-orange-500" />
                  )}
                  Obtener mi ubicación
                </button>
              </div>

              {mapError && (
                <div className="mt-3 p-3 bg-red-50 text-red-700 text-sm rounded-lg flex items-start">
                  <AlertTriangle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  {mapError}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
              <button
                onClick={closeMapDialog}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={confirmMapSelection}
                disabled={!tempCoordinates}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <Check className="h-4 w-4 mr-2" />
                Confirmar Ubicación
              </button>
            </div>
          </div>
        </div>
      )}

      {(showCategoryDropdown || showCityDropdown) && (
        <div className="fixed inset-0 z-10" onClick={() => {
          setShowCategoryDropdown(false);
          setShowCityDropdown(false);
        }} />
      )}

      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  Confirmar Registro de Negocio
                </h3>
                <button onClick={cancelRegistration} className="text-gray-400 hover:text-gray-600 transition-colors" disabled={loading}>
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4 text-sm text-gray-600">
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Nombre:</span>
                    <span>{businessData.businessName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Propietario:</span>
                    <span>{businessData.businessOwnerFirstName} {businessData.businessOwnerLastName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Email:</span>
                    <span>{businessData.businessEmail}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Teléfono:</span>
                    <span>{businessData.businessPhone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Categoría:</span>
                    <span>{businessData.businessCategory}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Año:</span>
                    <span>{businessData.businessYear}</span>
                  </div>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
                  <p className="font-medium text-orange-800 mb-1">Contraseña Temporal Generada:</p>
                  <p className="font-mono text-lg bg-white p-2 rounded border border-orange-200 text-center select-all">
                    {generatedPassword}
                  </p>
                  <p className="text-xs text-orange-700 mt-2">
                    * Esta contraseña será necesaria para el primer inicio de sesión del usuario.
                  </p>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button onClick={cancelRegistration} disabled={loading} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
                  Cancelar
                </button>
                <button onClick={confirmRegistration} disabled={loading} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Registrando...
                    </div>
                  ) : 'Confirmar Registro'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSuccessDialog && registrationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  ¡Negocio Registrado Exitosamente!
                </h3>

                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-500 block mb-1">Número de Registro</span>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-white border rounded px-3 py-2 font-mono text-lg font-bold text-gray-900 overflow-hidden text-ellipsis">
                        {registrationResult.business_registry_number}
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(registrationResult.business_registry_number);
                          setCopied('id');
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className={`p-2 rounded-md transition-colors ${copied === 'id' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        title="Copiar número de registro"
                      >
                        {copied === 'id' ? <Check className="h-5 w-5" /> : <Clipboard className="h-5 w-5" />}
                      </button>
                    </div>
                    {copied === 'id' && <p className="text-xs text-green-600 mt-1">¡Copiado al portapapeles!</p>}
                  </div>

                  <div className="pt-3 border-t border-gray-200">
                    <span className="text-sm font-medium text-gray-500 block mb-1">Credenciales Temporales</span>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-white border rounded px-3 py-2 font-mono text-sm overflow-hidden text-ellipsis">
                        {generatedPassword}
                      </div>
                      <button
                        onClick={copyToClipboard}
                        className={`p-2 rounded-md transition-colors ${copied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        title="Copiar contraseña"
                      >
                        {copied ? <Check className="h-5 w-5" /> : <Clipboard className="h-5 w-5" />}
                      </button>
                    </div>
                    {copied && <p className="text-xs text-green-600 mt-1">¡Copiado al portapapeles!</p>}
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  Se ha enviado un correo de bienvenida al propietario con estas credenciales e instrucciones para acceder.
                </p>

                <button onClick={closeSuccessDialog} className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors">
                  Continuar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}